import { useState, useRef, useEffect, useCallback } from "react";

const BASE = "/api";

// Chart.js loaded via CDN in index.html — accessed via window.Chart
const CHART_COLORS = [
  '#00d4aa','#0066ff','#ff6b35','#a855f7','#f59e0b',
  '#ec4899','#06b6d4','#84cc16','#ef4444','#8b5cf6'
];

const QUICK_QUESTIONS = [
  { icon:"📊", text:"Total production MTD by material" },
  { icon:"📅", text:"Daily production trend for Cadmium Ingots" },
  { icon:"🏭", text:"Which plant produced the most this month?" },
  { icon:"📦", text:"Show all dispatches MTD with quantities" },
  { icon:"🔄", text:"Top 5 materials by consumption MTD" },
  { icon:"📉", text:"Show closing stock for all materials" },
  { icon:"⚗️", text:"Cobalt plant production summary" },
  { icon:"🥇", text:"Which material has highest production FTD?" },
];

function escHtml(str) {
  return String(str)
    .replace(/&/g,'&amp;').replace(/</g,'&lt;')
    .replace(/>/g,'&gt;').replace(/"/g,'&quot;');
}

function fmtVal(v) {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!isNaN(n) && String(v).trim() !== '') return n.toLocaleString('en-IN', { maximumFractionDigits: 10, minimumFractionDigits: 0 });
  return String(v);
}

// ── Chart component ───────────────────────────────────────────
function DprChart({ cid, type, title, labels, datasets, onCanvasRef }) {
  const canvasRef = useRef(null);
  const chartRef  = useRef(null);

  useEffect(() => {
    if (!canvasRef.current || !window.Chart) return;
    if (chartRef.current) { chartRef.current.destroy(); }
    const isPie = type === 'pie' || type === 'doughnut';
    const ds = datasets.map((d, i) => {
      const col = CHART_COLORS[i % CHART_COLORS.length];
      const base = { label: d.label || '', data: d.data || [] };
      if (isPie) {
        base.backgroundColor = CHART_COLORS;
        base.borderColor     = CHART_COLORS.map(c => c + '80');
        base.borderWidth     = 1;
      } else {
        base.backgroundColor = type === 'line' ? col + '20' : col + 'cc';
        base.borderColor     = col;
        base.borderWidth     = type === 'line' ? 2 : 0;
        base.borderRadius    = type === 'bar' ? 4 : 0;
        base.fill            = type === 'line';
        base.tension         = 0.4;
        base.pointBackgroundColor = col;
        base.pointRadius     = 3;
      }
      return base;
    });
    if (onCanvasRef) onCanvasRef(canvasRef.current);
    chartRef.current = new window.Chart(canvasRef.current, {
      type,
      data: { labels: labels || [], datasets: ds },
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: {
          legend: {
            display: isPie || ds.length > 1,
            labels: { color:'#475569', font:{ family:'monospace', size:11 }, padding:16 }
          },
          tooltip: {
            backgroundColor:'#111820', borderColor:'#1c2535', borderWidth:1,
            titleColor:'#eef3fa', bodyColor:'#c9d4e0',
            callbacks: {
              label: ctx => ' ' + (ctx.dataset.label ? ctx.dataset.label + ': ' : '') + Number(ctx.raw).toLocaleString('en-IN')
            }
          }
        },
        scales: isPie ? {} : {
          x: { ticks:{ color:'#7a8fa8', font:{ size:10 }, maxRotation:45 }, grid:{ color:'rgba(0,0,0,.04)' } },
          y: { ticks:{ color:'#7a8fa8', font:{ size:10 }, callback: v => {
            if (v >= 1000) return (v/1000).toFixed(1)+'K';
            return v;
          }}, grid:{ color:'rgba(0,0,0,.04)' } }
        }
      }
    });
    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [type, labels, datasets]);

  return (
    <div className="dpr-chart-wrap">
      {title && <div className="dpr-chart-title">{title}</div>}
      <div style={{ position:'relative', height:280 }}>
        <canvas ref={canvasRef}></canvas>
      </div>
    </div>
  );
}

// ── Table component ───────────────────────────────────────────
function DprTable({ columns, rows, totalRows }) {
  return (
    <div className="dpr-tbl-wrap">
      <div className="dpr-tbl-header">
        <span>📋 {columns.length} columns</span>
        <span>{(totalRows||rows.length).toLocaleString()} rows{totalRows > rows.length ? ` (showing ${rows.length})` : ''}</span>
      </div>
      <div className="dpr-tbl-scroll">
        <table className="dpr-table">
          <thead>
            <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {columns.map(c => <td key={c} title={String(row[c] ?? '')}>{fmtVal(row[c])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── PDF download ──────────────────────────────────────────────
function downloadPDF(data, question) {
  const { columns=[], rows=[], answer='', highlight='' } = data;
  const dateStr = new Date().toLocaleDateString('en-IN');
  const timeStr = new Date().toLocaleTimeString('en-IN');

  // Capture any visible Chart.js canvas on the page
  let chartImgHtml = '';
  try {
    const canvases = document.querySelectorAll('canvas');
    let bestCanvas = null;
    canvases.forEach(c => {
      if (c.width > 100 && c.height > 100) bestCanvas = c;
    });
    if (bestCanvas) {
      const imgData = bestCanvas.toDataURL('image/png', 1.0);
      chartImgHtml = '<div style="margin:16px 0;text-align:center;">'
        + '<img src="' + imgData + '" style="max-width:100%;height:auto;border:1px solid #e2e8f0;border-radius:6px;" />'
        + '</div>';
    }
  } catch(e) {}

  let tableHtml = '';
  if (columns.length && rows.length) {
    const ths = columns.map(c => '<th>' + escHtml(c) + '</th>').join('');
    const trs = rows.map((r,i) => {
      const tds = columns.map(c => '<td>' + escHtml(fmtVal(r[c])) + '</td>').join('');
      return '<tr class="' + (i%2===0?'even':'odd') + '">' + tds + '</tr>';
    }).join('');
    tableHtml = '<table><thead><tr>' + ths + '</tr></thead><tbody>' + trs + '</tbody></table>';
  }

  const htmlParts = [
    '<!DOCTYPE html><html><head><meta charset="UTF-8"/><title>RGTPL DPR Analytics</title>',
    '<style>',
    'body{font-family:Arial,sans-serif;font-size:12px;color:#1e293b;margin:24px;}',
    'h1{font-size:17px;color:#1F4E79;border-bottom:2px solid #1F4E79;padding-bottom:8px;}',
    '.meta{font-size:11px;color:#6B7280;margin-bottom:14px;}',
    '.hl{background:#f0fdf4;border:1px solid #a7f3d0;padding:9px 12px;font-weight:700;color:#065f46;margin-bottom:10px;border-radius:4px;}',
    '.ans{background:#F8FAFC;border:1px solid #E5E7EB;padding:10px;margin-bottom:14px;line-height:1.6;border-radius:4px;}',
    'h2{font-size:13px;color:#374151;margin:14px 0 6px;}',
    'table{width:100%;border-collapse:collapse;font-size:11px;}',
    'th{background:#1F4E79;color:#fff;padding:7px 10px;text-align:left;}',
    'td{padding:5px 10px;border-bottom:1px solid #E5E7EB;}',
    'tr.even td{background:#F8FAFC;}',
    '@media print{body{margin:10px;}}',
    '</style></head><body>',
    '<h1>RGTPL Production Analytics Report</h1>',
    '<div class="meta">Generated: ' + dateStr + ' ' + timeStr + ' &nbsp;|&nbsp; ' + escHtml(question) + '</div>',
    highlight ? '<div class="hl">✦ &nbsp;' + escHtml(highlight) + '</div>' : '',
    answer    ? '<div class="ans">' + escHtml(answer) + '</div>' : '',
    chartImgHtml ? '<h2>Chart</h2>' + chartImgHtml : '',
    rows.length  ? '<h2>Data (' + rows.length + ' rows)</h2>' + tableHtml : '',
    '</body></html>'
  ];

  const html = htmlParts.join('');
  const blob = new Blob([html], { type:'text/html' });
  const url  = URL.createObjectURL(blob);
  const w    = window.open(url, '_blank');
  if (w) {
    w.addEventListener('load', () => { setTimeout(() => w.print(), 600); });
    setTimeout(() => URL.revokeObjectURL(url), 15000);
  }
}


// ── Excel download ────────────────────────────────────────────
function downloadExcel(data, question) {
  const { columns=[], rows=[] } = data;
  if (!columns.length && !rows.length) return;

  // Capture chart image as base64 if available
  let chartBase64 = null;
  try {
    const canvases = document.querySelectorAll('canvas');
    let bestCanvas = null;
    canvases.forEach(c => { if (c.width > 100 && c.height > 100) bestCanvas = c; });
    if (bestCanvas) chartBase64 = bestCanvas.toDataURL('image/png', 1.0).split(',')[1];
  } catch(e) {}

  // Build worksheet data
  const wsData = [];

  // Title rows
  wsData.push(['RGTPL Production Analytics Report']);
  wsData.push(['Query: ' + question]);
  wsData.push(['Generated: ' + new Date().toLocaleString('en-IN')]);
  wsData.push([]);

  // Headers
  wsData.push(columns);

  // Data rows
  rows.forEach(r => {
    wsData.push(columns.map(c => {
      const v = r[c];
      if (v === null || v === undefined) return '';
      const n = Number(v);
      return !isNaN(n) && String(v).trim() !== '' ? n : String(v);
    }));
  });

  // Use SheetJS if available
  if (window.XLSX) {
    const wb = window.XLSX.utils.book_new();
    const ws = window.XLSX.utils.aoa_to_sheet(wsData);

    // Style column widths
    const colWidths = columns.map(c => ({
      wch: Math.max(c.length, ...rows.map(r => String(r[c] ?? '').length), 12)
    }));
    ws['!cols'] = colWidths;

    window.XLSX.utils.book_append_sheet(wb, ws, 'Analytics');

    // If chart captured, add image sheet with instructions
    if (chartBase64) {
      const imgWs = window.XLSX.utils.aoa_to_sheet([
        ['Chart is embedded below — Chart image: ' + question],
        ['Note: Open the PDF download to see the chart rendered inline.'],
      ]);
      window.XLSX.utils.book_append_sheet(wb, imgWs, 'Chart Note');
    }

    const filename = 'DPR_Analytics_' + new Date().toISOString().slice(0,10) + '.xlsx';
    window.XLSX.writeFile(wb, filename);
    return;
  }

  // Fallback: CSV with BOM
  const bom = '﻿';
  const csvRows = wsData.map(row =>
    row.map(cell => {
      const s = String(cell ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? '"' + s.replace(/"/g, '""') + '"'
        : s;
    }).join(',')
  );
  const csv  = bom + csvRows.join('\n');
  const blob = new Blob([csv], { type:'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = 'DPR_Analytics_' + new Date().toISOString().slice(0,10) + '.csv';
  a.click();
  URL.revokeObjectURL(url);
}


// ── Bot Message component ─────────────────────────────────────
function BotMessage({ data, question, msgId }) {
  const [viewType, setViewType] = useState(data.type || 'table');
  const canvasRef = useRef(null);
  const chartTypes = ['bar','line','pie','doughnut'];
  const hasChart = chartTypes.includes(data.type) && data.chart_labels && data.chart_datasets;
  const hasTable = data.columns?.length > 0 && data.rows?.length > 0;

  if (data.error) {
    return (
      <div className="dpr-bubble dpr-bubble-error">
        ❌ <strong>Error:</strong> {data.error}
      </div>
    );
  }

  return (
    <div className="dpr-bubble">
      {data.highlight && (
        <div className="dpr-highlight">
          <span style={{fontSize:16}}>✦</span>
          {data.highlight}
        </div>
      )}
      {data.answer && (
        <p style={{fontSize:13,lineHeight:1.7,color:'#1e293b',marginBottom:8}}>{data.answer}</p>
      )}

      {/* View toggle */}
      {(hasChart || hasTable) && (
        <div className="dpr-view-toggle">
          {hasChart && <>
            {['bar','line','pie','doughnut'].map(t => (
              <button key={t} className={`dpr-vt-btn ${viewType===t?'active':''}`}
                onClick={() => setViewType(t)}>
                {t==='bar'?'📊':t==='line'?'📈':t==='pie'?'🥧':'🍩'} {t.charAt(0).toUpperCase()+t.slice(1)}
              </button>
            ))}
          </>}
          {hasTable && (
            <button className={`dpr-vt-btn ${viewType==='table'?'active':''}`}
              onClick={() => setViewType('table')}>
              📋 Table
            </button>
          )}
        </div>
      )}

      {/* Chart */}
      {hasChart && viewType !== 'table' && (
        <DprChart cid={`chart-${msgId}`} type={viewType}
          title={data.chart_title} labels={data.chart_labels} datasets={data.chart_datasets}
          onCanvasRef={ref => { canvasRef.current = ref; }} />
      )}

      {/* Table */}
      {hasTable && viewType === 'table' && (
        <DprTable columns={data.columns} rows={data.rows} totalRows={data.total_rows} />
      )}

      {/* SQL + Download buttons */}
      {(data.rows?.length > 0 || data.answer) && (
        <SqlDownloadBar data={data} question={question} msgId={msgId} />
      )}
    </div>
  );
}

function SqlDownloadBar({ data, question, msgId }) {
  return (
    <div style={{marginTop:10}}>
      <div style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
        <button className="dpr-dl-btn" onClick={() => downloadExcel(data, question)}>
          ⬇ Excel
        </button>
        <button className="dpr-dl-btn dpr-dl-pdf" onClick={() => downloadPDF(data, question, msgId)}>
          🖨 PDF
        </button>
      </div>
    </div>
  );
}

// ── Main Chatbot component ────────────────────────────────────
export default function Chatbot() {
  const [messages, setMessages] = useState([]);
  const [input,    setInput]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const chatRef  = useRef(null);
  const inputRef = useRef(null);
  const msgIdRef = useRef(0);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages]);

  const ask = useCallback(async (question) => {
    if (!question.trim() || loading) return;
    setInput('');
    const id = ++msgIdRef.current;
    setMessages(m => [...m, { id, role:'user', text:question }, { id:id+0.5, role:'typing' }]);
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/chat`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ question })
      });
      const data = await r.json();
      setMessages(m => m.filter(x => x.role !== 'typing').concat({ id:id+1, role:'bot', data, question }));
    } catch(e) {
      setMessages(m => m.filter(x => x.role !== 'typing').concat({ id:id+1, role:'bot', data:{error:'Network error: '+e.message}, question }));
    } finally { setLoading(false); inputRef.current?.focus(); }
  }, [loading]);

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(input); }
  };

  return (
    <div className="dpr-layout">
      {/* Sidebar */}
      <aside className="dpr-sidebar">
        <div className="dpr-sidebar-logo">
          <div className="dpr-logo-sq">HZ</div>
          <div>
            <div className="dpr-logo-title">RGTPL Analytics</div>
            <div className="dpr-logo-sub">SQL · AI · GPT-4o mini</div>
          </div>
        </div>

        <div className="dpr-sidebar-section">
          <div className="dpr-section-label">Quick Questions</div>
          {QUICK_QUESTIONS.map((q, i) => (
            <button key={i} className="dpr-quick-btn" onClick={() => ask(q.text)}>
              <span className="dpr-quick-icon">{q.icon}</span>
              <span className="dpr-quick-text">{q.text}</span>
            </button>
          ))}
        </div>

        <div className="dpr-sidebar-tables">
          <div className="dpr-section-label">Connected Tables</div>
          {[
            {name:'sap_movements',  dot:'#0ea5e9', cols:'9 cols'},
            {name:'closing_stock',  dot:'#3b82f6', cols:'9 cols'},
            {name:'uploads',        dot:'#f97316', cols:'5 cols'},
          ].map(t => (
            <div key={t.name} className="dpr-table-chip">
              <div className="dpr-table-dot" style={{background:t.dot}}></div>
              <div className="dpr-table-name">{t.name}</div>
              <div className="dpr-table-count">{t.cols}</div>
            </div>
          ))}
        </div>
      </aside>

      {/* Main */}
      <div className="dpr-main">
        <div className="dpr-topbar">
          <div>
            <div className="dpr-topbar-title">DPR Production Intelligence</div>
            <div className="dpr-topbar-sub">Ask anything about production, consumption, dispatches, stock</div>
          </div>
          <div className="dpr-status-pill">
            <div className="dpr-status-dot"></div> Live
          </div>
        </div>

        <div className="dpr-chat-area" ref={chatRef}>
          {messages.length === 0 && (
            <div className="dpr-welcome">
              <div className="dpr-welcome-icon">⚡</div>
              <h2>RGTPL DPR Analytics AI</h2>
              <p>Ask me anything about production, consumption, dispatches, stock levels, material quantities and trends. I'll respond with charts, tables, and summaries.</p>
              <div className="dpr-welcome-tags">
                <span className="dpr-welcome-tag">Plain English questions</span>
                <span className="dpr-welcome-tag">Auto charts & tables</span>
                <span className="dpr-welcome-tag">Excel & PDF download</span>
                <span className="dpr-welcome-tag">Live database</span>
              </div>
            </div>
          )}

          {messages.map(msg => (
            <div key={msg.id} className={`dpr-msg ${msg.role === 'user' ? 'dpr-msg-user' : 'dpr-msg-bot'}`}>
              <div className="dpr-msg-avatar">
                {msg.role === 'user' ? '👤' : msg.role === 'typing' ? '⚡' : '⚡'}
              </div>
              <div className="dpr-msg-body">
                {msg.role === 'user' && (
                  <div className="dpr-bubble dpr-bubble-user">{msg.text}</div>
                )}
                {msg.role === 'typing' && (
                  <div className="dpr-typing">
                    <div className="dpr-dot"></div>
                    <div className="dpr-dot"></div>
                    <div className="dpr-dot"></div>
                  </div>
                )}
                {msg.role === 'bot' && (
                  <BotMessage data={msg.data} question={msg.question} msgId={msg.id} />
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="dpr-input-area">
          <div className="dpr-input-wrap">
            <textarea ref={inputRef} className="dpr-input" rows={1}
              placeholder="Ask about production, stock, dispatches, materials…"
              value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKey}
              style={{resize:'none'}}
            />
            <button className="dpr-send-btn" onClick={() => ask(input)} disabled={loading || !input.trim()}>
              ➤
            </button>
          </div>
          <div className="dpr-input-hint">Press Enter to send · Shift+Enter for new line</div>
        </div>
      </div>
    </div>
  );
}
